import { useToast } from "@/components/podkit/toasts/use-toast";
import { useListPreferences, useSetPreference } from "@/queries/user-preferences-queries";
import { formatError } from "@/utils/errors";
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * useContextURL is a hook that provides a context URL from the hash of the current location
 *
 * @returns contextURL: the URL from the hash of the current location
 */
export function useContextURL(initialContextURL: string) {
    const location = useLocation();
    const navigate = useNavigate();

    const [contextURL, setContextURL] = useState<string | undefined>();
    const [contextURLValid, setContextURLValid] = useState<boolean>(false);

    const updateContextURL = useCallback((newUrl: string) => {
        setContextURL(newUrl);
        try {
            setContextURLValid(new URL(newUrl.toLocaleLowerCase()).pathname.length > 3);
        } catch {
            setContextURLValid(false);
        }
    }, []);

    const isValidURL = useCallback((url: string): boolean => URL.canParse(url), []);
    // Handle initial URL
    useEffect(() => {
        if (initialContextURL && isValidURL(initialContextURL)) {
            updateContextURL(initialContextURL);
        }
    }, [initialContextURL, isValidURL, updateContextURL]);
    useEffect(() => {
        const newUrl = (location.hash || "#").slice(1).trim();
        if (!contextURL && isValidURL(newUrl)) {
            updateContextURL(newUrl);
            navigate({ hash: "" }); // after we initialize the contextURL, we can clean up
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        contextURL,
        setContextURL: updateContextURL,
        contextURLValid,
    };
}

const CONTEXT_URLS = "CONTEXT_URLS";

export function useListContextUrls() {
    const { preferences, isPending: isPendingPreferences } = useListPreferences();

    let contextUrls: string[] | undefined;
    const preferenceValue = preferences?.find((p) => p.key === CONTEXT_URLS)?.value;
    if (preferenceValue) {
        try {
            contextUrls = JSON.parse(preferenceValue) as string[];
        } catch (e) {
            console.error(`Failed to parse ${CONTEXT_URLS} preference`, e);
        }
    }

    return {
        contextUrls,
        isPending: isPendingPreferences,
    };
}

export function useSetContextUrls() {
    const { toast } = useToast();
    const setPreference = useSetPreference();

    return useCallback(
        async (contextUrls: string[]) => {
            try {
                let value = JSON.stringify(contextUrls.slice(0, 50));
                if (value.length >= 2480) {
                    // Guard against max length for user preference value defined in proto
                    value = JSON.stringify(contextUrls.slice(0, 30));
                }
                await setPreference.mutateAsync({ key: CONTEXT_URLS, value });
            } catch (e) {
                toast({
                    title: "Failed to update context urls",
                    description: formatError(e),
                });
            }
        },
        [setPreference, toast],
    );
}
